// Melbourne wall-clock <-> UTC instant, for sale windows entered in the admin
// console. Racing runs on Melbourne time whatever zone the admin's browser is
// in, so a datetime-local input is read as Melbourne time (AEST +10 / AEDT +11)
// and stored as a UTC ISO instant. Intl carries the DST rules: no date library.

export const MELBOURNE_TZ = 'Australia/Melbourne';

const PARTS_FORMAT = new Intl.DateTimeFormat('en-AU', {
  timeZone: MELBOURNE_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function melbourneWallClock(epochMs: number): WallClock {
  const parts: Record<string, number> = {};
  for (const p of PARTS_FORMAT.formatToParts(new Date(epochMs))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  }
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour === 24 ? 0 : parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
}

/** Melbourne's offset from UTC at an instant, in ms (+10h or +11h). */
function melbourneOffsetMs(epochMs: number): number {
  const w = melbourneWallClock(epochMs);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(epochMs / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** A UTC ISO instant as a datetime-local value ('YYYY-MM-DDTHH:mm') on Melbourne's clock; '' for none. */
export function utcIsoToMelbourneInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const w = melbourneWallClock(ms);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

/** A datetime-local value read as Melbourne time, as a UTC ISO instant; null for empty or invalid. */
export function melbourneInputToUtcIso(value: string | null | undefined): string | null {
  const m = value ? /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value) : null;
  if (!m) return null;
  const wallAsUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0));
  // The offset depends on the instant we are solving for, so take it at a first
  // guess and again at the result; two passes settle either side of a DST change.
  const firstGuess = wallAsUtc - melbourneOffsetMs(wallAsUtc);
  const instant = wallAsUtc - melbourneOffsetMs(firstGuess);
  return new Date(instant).toISOString();
}

const SHORT_FORMAT = new Intl.DateTimeFormat('en-AU', {
  timeZone: MELBOURNE_TZ,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/** A UTC instant as a short Melbourne date and time, e.g. "2 Nov 2026, 9:00 pm". */
export function formatMelbourne(iso: string): string {
  return SHORT_FORMAT.format(new Date(iso));
}
